import type { Quest, QuestDoc, QuestRecordOverrides } from './schemas'

/**
 * A change to one quest's record overrides, expressed against whatever is stored
 * at the moment the write runs rather than against a render-time snapshot.
 *
 * Return the argument unchanged to mean "nothing to do". Identity is the signal,
 * because `undefined` is a legal result: it clears every override.
 */
export type RecordOverridesMutation = (
  current: QuestRecordOverrides | undefined
) => QuestRecordOverrides | undefined

export interface QuestRepository {
  list(): Promise<QuestDoc[]>
  show(id: string): Promise<QuestDoc | undefined>
  create(entity: Quest): Promise<string>
  update(id: string, entity: Quest): Promise<void>
  setEnabled(id: string, enabled: boolean): Promise<void>
  rename(id: string, name: string): Promise<void>
  /**
   * Applies `mutate` to the STORED overrides and writes the result, leaving every
   * other field of the quest as it is on disk.
   *
   * Field scoped and read-modify-write, like `setEnabled` and `rename`, and for a
   * sharper reason (ZL-1113). Every override write used to send a whole entity
   * rebuilt from the render snapshot, so two writes issued before the quest query
   * refreshed both derived from the same snapshot and the later one discarded the
   * earlier field — and reverted `enabled` or `name` if either had changed
   * meanwhile.
   *
   * Resolves `true` when the document changed, so a caller can skip
   * invalidating the quest list — and with it a scenario rebuild — after a write
   * that turned out to be a no-op.
   */
  updateRecordOverrides(
    id: string,
    mutate: RecordOverridesMutation
  ): Promise<boolean>
  delete(id: string): Promise<void>
}

let _repository: QuestRepository | null = null

export const setQuestRepository = (repo: QuestRepository): void => {
  _repository = repo
}

export const getQuestRepository = (): QuestRepository => {
  if (!_repository) throw new Error('QuestRepository not initialized')
  return _repository
}

// Returns true only when a quest repo has been registered. Callers that need
// to work in both quest-aware (mobile) and quest-blind (portal) contexts can
// gate on this to skip quest-side side effects instead of throwing.
export const hasQuestRepository = (): boolean => _repository !== null
